import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import { Headphones, Search, Filter, X, ExternalLink, RefreshCw, Loader2, Award } from "lucide-react";
import { usePermission } from "../hooks/usePermission";

interface Ticket {
  id: string;
  whmcsTicketId: number;
  tid: string;
  subject: string;
  clientName: string | null;
  clientEmail: string | null;
  department: string | null;
  priority: string | null;
  status: string;
  admin: string | null;
  respondingAdmins?: string | null;
  lastReplyAt: string | null;
  createdAt: string;
  updatedAt: string;
}

function statusColor(s: string): string {
  const k = s.toLowerCase();
  if (k.includes("open")) return "bg-blue-100 text-blue-700";
  if (k.includes("customer")) return "bg-orange-100 text-orange-700";
  if (k.includes("answered")) return "bg-green-100 text-green-700";
  if (k.includes("progress")) return "bg-yellow-100 text-yellow-700";
  if (k.includes("hold")) return "bg-amber-100 text-amber-700";
  if (k.includes("closed")) return "bg-gray-100 text-gray-600";
  return "bg-gray-100 text-gray-700";
}

function fmtDate(dt: string | null): string {
  if (!dt) return "—";
  return new Date(dt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function WhmcsTickets() {
  const navigate = useNavigate();
  const canQuality = usePermission("quality", "create");
  const [rows, setRows] = useState<Ticket[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [department, setDepartment] = useState("");
  const [admin, setAdmin] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState<"tid" | "updatedAt">("tid");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const limit = 25;

  const load = () => {
    setLoading(true);
    const params: any = { page, limit, sortBy, sortDir };
    if (search.trim()) params.search = search.trim();
    if (status) params.status = status;
    if (department) params.department = department;
    if (admin) params.admin = admin;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    api.get("/whmcs/tickets", { params })
      .then(r => { setRows(r.data.rows || []); setTotal(r.data.total || 0); })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, status, department, admin, dateFrom, dateTo, sortBy, sortDir]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const distinctStatuses = Array.from(new Set(rows.map(r => r.status).filter(Boolean))).sort();
  const distinctDepts = Array.from(new Set(rows.map(r => r.department).filter(Boolean))).sort();
  const distinctAdmins = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => {
      const names = (r.respondingAdmins || r.admin || "").split(",").map(s => s.trim()).filter(Boolean);
      names.forEach(n => set.add(n));
    });
    return Array.from(set).sort();
  }, [rows]);
  const hasFilters = search || status || department || admin || dateFrom || dateTo;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  // Per-department ticket counts (from current page rows)
  const deptCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    rows.forEach(r => {
      const key = r.department || "Unassigned";
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const syncNow = async () => {
    setSyncing(true);
    try { await api.post("/whmcs/sync-now"); load(); }
    catch {} finally { setSyncing(false); }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <Headphones className="text-blue-500" size={26}/>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">WHMCS Tickets</h1>
            <p className="text-sm text-gray-500">{total.toLocaleString()} tickets synced</p>
          </div>
        </div>
        <button onClick={syncNow} disabled={syncing}
          className="flex items-center gap-2 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-50">
          {syncing ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
          Sync Now
        </button>
      </div>

      {/* Per-department cards */}
      {deptCounts.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <div
            onClick={() => setDepartment("")}
            className={`bg-white rounded-xl border p-4 text-center cursor-pointer transition-all ${
              !department ? "ring-2 ring-offset-1 ring-blue-400 border-blue-300" : "border-gray-200 hover:border-gray-300"
            }`}>
            <div className="text-xs text-gray-500">All</div>
            <div className="text-2xl font-bold text-gray-800">{total.toLocaleString()}</div>
          </div>
          {deptCounts.map(([name, count]) => {
            const active = department === name;
            return (
              <div key={name}
                onClick={() => setDepartment(active ? "" : name)}
                className={`bg-white rounded-xl border p-4 text-center cursor-pointer transition-all ${
                  active ? "ring-2 ring-offset-1 ring-blue-400 border-blue-300" : "border-gray-200 hover:border-gray-300"
                }`}>
                <div className="text-xs text-gray-500 truncate" title={name}>{name}</div>
                <div className="text-2xl font-bold text-gray-800">{count}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4">
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
          <Filter size={13}/> Filters
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[10px] font-normal text-gray-500">Sort:</span>
            <select
              value={`${sortBy}:${sortDir}`}
              onChange={e => {
                const [sb, sd] = e.target.value.split(":");
                setSortBy(sb as any); setSortDir(sd as any); setPage(1);
              }}
              className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white font-normal">
              <option value="tid:desc">Ticket # — Newest first</option>
              <option value="tid:asc">Ticket # — Oldest first</option>
              <option value="updatedAt:desc">Last update — Newest</option>
              <option value="updatedAt:asc">Last update — Oldest</option>
            </select>
            {hasFilters && (
              <button onClick={() => { setSearch(""); setStatus(""); setDepartment(""); setAdmin(""); setDateFrom(""); setDateTo(""); setPage(1); }}
                className="flex items-center gap-1 text-red-500 hover:text-red-700 font-normal">
                <X size={11}/> Reset
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search TID, subject, client..."
              className="w-full pl-9 border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
          </div>
          <select value={status} onChange={e => setStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">All Statuses</option>
            {distinctStatuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={department} onChange={e => setDepartment(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">All Departments</option>
            {distinctDepts.map(d => <option key={d!} value={d!}>{d}</option>)}
          </select>
          <select value={admin} onChange={e => setAdmin(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">All Employees</option>
            {distinctAdmins.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <div>
            <label className="block text-[10px] text-gray-500 mb-0.5 ml-1">Date from (YYYY-MM-DD)</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"/>
            {dateFrom && <div className="text-[10px] text-blue-600 mt-0.5 ml-1">→ {dateFrom}</div>}
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-0.5 ml-1">Date to (YYYY-MM-DD)</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"/>
            {dateTo && <div className="text-[10px] text-blue-600 mt-0.5 ml-1">→ {dateTo}</div>}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-gray-500">Loading tickets...</div>
      ) : rows.length === 0 ? (
        <div className="p-6 bg-gray-50 rounded-xl text-center text-gray-400">
          No tickets found. Try syncing or adjust the filters.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {["TID", "Subject", "Client", "Department", "Status", "Last Reply", "Created", ...(canQuality ? ["Quality"] : [])].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(t => (
                <tr key={t.id} onClick={() => window.open(`/whmcs-tickets/${t.id}`, "_blank", "noopener")}
                  className="hover:bg-blue-50 cursor-pointer">
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{t.tid || t.whmcsTicketId}</td>
                  <td className="px-4 py-3 font-medium text-gray-900 max-w-md truncate">{t.subject}</td>
                  <td className="px-4 py-3 text-gray-700 text-xs">
                    <div>{t.clientName || "—"}</div>
                    {t.clientEmail && <div className="text-gray-400">{t.clientEmail}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{t.department || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(t.status)}`}>{t.status}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(t.lastReplyAt)}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(t.createdAt)}</td>
                  {canQuality && (
                    <td className="px-4 py-3 text-center">
                      <button
                        title="Add Quality Review"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/quality?ref=${encodeURIComponent(t.tid || String(t.whmcsTicketId))}&type=TICKET`);
                        }}
                        className="inline-flex items-center justify-center w-8 h-8 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100">
                        <Award size={14}/>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
              <div>Page {page} of {totalPages}</div>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="border border-gray-300 rounded px-3 py-1 hover:bg-gray-50 disabled:opacity-40">Previous</button>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                  className="border border-gray-300 rounded px-3 py-1 hover:bg-gray-50 disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
